/**
 * Reports whether an `IntersectionObserver` batch leaves the observed element
 * on screen, for a gate that reveals once and then stops observing.
 *
 * One callback can carry several records for a single target. The observer
 * queues a record per observation cycle and delivers everything that collected
 * before the callback ran, so a busy frame hands over a batch rather than the
 * one record the target's current state would suggest. Reading the first record
 * therefore answers a question about the past: a virtualized row mounts ahead
 * of the viewport, its opening record is a miss, and a batch that also holds
 * the record for that row arriving on screen would be discarded on the strength
 * of the stale one. Nothing fires again while the row sits still, and a reveal
 * that never runs leaves its element hidden for as long as it stays there.
 *
 * The last record is the observer's own current view of the target, and it is
 * the state a later cycle compares against when deciding whether to queue
 * anything at all. Reading it keeps the gate in step with the observer in both
 * directions: a hit is acted on immediately, and a miss is answered by a
 * further record whenever the target does arrive.
 */
export function hasEnteredView(
  entries: readonly IntersectionObserverEntry[]
): boolean {
  return entries.at(-1)?.isIntersecting ?? false;
}
